import { Form } from "antd/es";
import { ColumnType } from "antd/es/table";
import _ from "lodash";
import React, { PureComponent } from "react";
import withCommonEvents from "../../../../shared/hoc/with-common-events";
import { CommonProps } from "../../common/common-props";

export interface TableDataProps extends ColumnType<any> {
  rowData?: any;
  children?: React.ReactNode;
  isEditableColumn?: boolean;
  isEditMode?: boolean;
  visibilityCondition?: (rowData: object) => boolean;
  editabilityCondition?: (rowData: object) => boolean;
}

declare let window: any;
interface KTableDataState {}

class TableData<P, S> extends PureComponent<TableDataProps & P & CommonProps, KTableDataState & S> {
  constructor(props: TableDataProps & P & CommonProps) {
    super(props);
  }

  static dataFieldKey: RegExp = /\[datafield:((?:(?!\[datafield:).)*)\]/;

  static oldValue: RegExp = /\[datafield:((?:(?!\[datafield:).)*)\]/g;

  private static camelCaseCache = new Map<string, string>();
  private static camelCaseRegex = /^([A-Z])|[\s-](\w)/g;
  private static pathFieldsCache = new Map<string, string[]>();
  private static pathFieldsRawCache = new Map<string, string[]>();

  static toCamelCase = (str: string): string => {
    let cached = TableData.camelCaseCache.get(str);
    if (cached !== undefined) return cached;

    cached = str.replace(TableData.camelCaseRegex, (match, p1, p2) => {
      if (p2) {
        return p2.toUpperCase();
      }

      return p1.toLowerCase();
    });
    TableData.camelCaseCache.set(str, cached);
    return cached;
  };

  // Raw (non-camelCased) path segments; getValueFromPath falls back to these.
  private static resolvePathFieldsRaw = (path: string): string[] => {
    let cached = TableData.pathFieldsRawCache.get(path);
    if (cached) return cached;

    cached = path.split(".").filter((field: string, index: number, array: string[]) => {
      const theFieldIndexContaingSelected = array.findIndex((x) => x.includes("[selected]"));

      if (theFieldIndexContaingSelected > -1) {
        return index > theFieldIndexContaingSelected;
      }

      return true;
    });
    TableData.pathFieldsRawCache.set(path, cached);
    return cached;
  };

  private static resolvePathFields = (path: string): string[] => {
    let cached = TableData.pathFieldsCache.get(path);
    if (cached) return cached;

    cached = TableData.resolvePathFieldsRaw(path).map((x) => TableData.toCamelCase(x));
    TableData.pathFieldsCache.set(path, cached);
    return cached;
  };

  static getValueFromPath = (data: object, path: string, index: number) => {
    // Example path: "Users[first].Name
    const fields = TableData.resolvePathFields(path);
    const rawFields = TableData.resolvePathFieldsRaw(path);

    const value = fields.reduce((prev: any, curr: string, i: number) => {
      if (!prev) return "";

      const raw: string = rawFields[i] ?? curr;

      // camelCase key first (existing behavior), then raw key so PascalCase
      // datasource fields (e.g. "Photo") resolve in the carousel path too.
      const twoKeyGet = (camelKey: string, rawKey: string) => {
        const camelValue = prev?.[camelKey];
        if (camelValue !== undefined && camelValue !== null) return camelValue;
        return prev?.[rawKey];
      };

      if (curr.endsWith("[first]")) {
        return twoKeyGet(curr.replace("[first]", ""), raw.replace("[first]", ""))?.at?.(0);
      } else if (curr.endsWith("[last]")) {
        return twoKeyGet(curr.replace("[last]", ""), raw.replace("[last]", ""))?.at?.(-1);
      } else if (curr.endsWith("[selected]")) {
        return twoKeyGet(curr.replace("[selected]", ""), raw.replace("[selected]", ""))?.at?.(index);
      }

      return twoKeyGet(curr, raw);
    }, data);

    return value;
  };

  static getIsDataRepeater = (child: any): boolean => {
    if (!child) return false;

    if (child?.props?.dataSource && child.props.dataSource?.length > 0) {
      return true;
    }

    return false;
  };

  // antd's Form.Item (rc-field-form `Field`) always overwrites its child's own
  // value: getControlled() builds `{ ...childProps, ...getValueProps(formValue) }`
  // and clones the child with it, so the form-store value for `name` wins. Inside
  // a DataRepeater row that is wrong — the per-row value substituted from
  // `[datafield:X]` below is replaced by the screen-level, row-agnostic form
  // field, which for repeater children is never seeded and therefore resolves to
  // undefined. Components wrapped with a Form.Item (Image, and any other
  // `wrapWithFormItem` element) then render their empty state — for Image the
  // "no image" placeholder — in every row of a Carousel / HorizontalGrid /
  // GalleryView / KanbanBoard (KOA-18127). Table columns escaped it only because
  // KuikaEditableCell (KOA-13928) sits in between and re-injects the row value.
  //
  // An empty `getValueProps` result makes rc-field-form contribute no value prop
  // at all, so the row-substituted value survives. The change trigger is added
  // separately by getControlled(), so edits still reach the form store and
  // action bindings reading `form.getFieldValue(...)` keep working.
  private static readonly keepRowSubstitutedValue = (): object => ({});

  // A cell value can also be static — a Symbol Picker → UploadedImages GUID, a
  // literal URL — with no `[datafield:X]` token for the scan below to find. Those
  // are emitted inline on the element and, like the row-bound ones, never reach
  // the form store: codegen's IsFormItemElement() returns false for every
  // table/repeater child, so fillFormInitValues() skips them. The named
  // Form.Item still injects, so the child received `undefined` and an <Image>
  // rendered the "no image" placeholder (KOA-18281).
  //
  // Here the form field cannot simply be ignored the way keepRowSubstitutedValue
  // ignores it: nothing else is row-specific, so a runtime write — SetValueOf,
  // setFieldsValue — must still win. Gate on whether the field carries a value
  // at all. Unseeded (`undefined`) yields to the child; anything else, `null`
  // included, is a deliberate write and keeps rc-field-form's own semantics.
  private static readonly deferToFormCache: Map<string, (val: unknown) => object> = new Map<
    string,
    (val: unknown) => object
  >();

  private static deferToFormOnlyWhenSeeded = (valuePropName: string): ((val: unknown) => object) => {
    const cached: ((val: unknown) => object) | undefined = TableData.deferToFormCache.get(valuePropName);
    if (cached !== undefined) {
      return cached;
    }

    // One stable identity per prop name: this runs per cell per row, and a fresh
    // closure would make every row's Form.Item look like changed props.
    const deferred: (val: unknown) => object = (val: unknown): object =>
      val === undefined ? {} : { [valuePropName]: val };
    TableData.deferToFormCache.set(valuePropName, deferred);
    return deferred;
  };

  // The scan below runs per cell per row, but its input is the immutable
  // pre-substitution element tree, which every row of a repeater shares. Cache
  // on node identity so a 1000-row table walks each cell template once. Keyed by
  // the prop name being scanned, since a cell can be reached with either.
  private static rowBoundValuePropCache = new Map<string, WeakMap<object, boolean>>();

  /**
   * True when the value prop rc-field-form would overwrite (`value`, or the
   * Form.Item's `valuePropName`) carries a `[datafield:X]` token somewhere in the
   * subtree — i.e. that exact prop is row-bound and must keep the row's value.
   * Deliberately scoped to that one prop: a cell bound only through some other
   * prop (`tooltip`, `visExField1`, …) has no row-bound value of its own, so the
   * form field keeps ownership of it exactly as before. Scanned on the
   * pre-substitution element tree, so the tokens are still intact.
   */
  static hasRowBoundValueProp = (node: React.ReactNode, valuePropName: string): boolean => {
    let cache: WeakMap<object, boolean> | undefined = TableData.rowBoundValuePropCache.get(valuePropName);
    if (!cache) {
      cache = new WeakMap<object, boolean>();
      TableData.rowBoundValuePropCache.set(valuePropName, cache);
    }

    const cacheKey: object | null = node !== null && typeof node === "object" ? (node as object) : null;
    if (cacheKey !== null) {
      const cached: boolean | undefined = cache.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
    }

    let found: boolean = false;

    React.Children.forEach(node, (child: React.ReactNode) => {
      if (found === true || !React.isValidElement(child)) {
        return;
      }

      const boundValue: unknown = child.props[valuePropName];
      const carriesToken: boolean = typeof boundValue === "string" && boundValue.includes("datafield:");

      if (carriesToken === true || TableData.hasRowBoundValueProp(child.props.children, valuePropName) === true) {
        found = true;
      }
    });

    if (cacheKey !== null) {
      cache.set(cacheKey, found);
    }

    return found;
  };

  static setDataFieldValues = (
    children: any,
    rowData?: any,
    rowIndex?: number,
    _visibilityCondition?: (rowData: object) => boolean,
    _editabilityCondition?: (rowData: object) => boolean
  ): React.ReactNode[] => {
    if (!children) return [];

    if (window.kuika.isDesignTime) {
      return children;
    }

    return React.Children.map(children, (child, childIndex) => {
      const props: Record<string, any> = {};
      let manipulatedChildren: React.ReactNode[] = [];

      if (child?.props) {
        const visibilityCondition = child.props?.visibilityCondition || _visibilityCondition;

        if (visibilityCondition) {
          if (child?.props?.visibilityByAuthorization === "hidden") {
            props.visibility = "hidden";
          } else {
            props.visibility = visibilityCondition(rowData) === true ? "visible" : "hidden";
          }
        }

        const editabilityCondition = child.props?.editabilityCondition || _editabilityCondition;

        if (editabilityCondition) {
          if (child?.props?.editabilityByAuthorization === "disabled") {
            props.editability = "disabled";
          } else {
            props.editability = editabilityCondition(rowData) === true ? "enabled" : "disabled";
          }
        }

        Object?.keys(child.props)?.forEach((key) => {
          if (typeof child.props[key] === "string") {
            if (child.props[key]?.toString().indexOf("datafield:") > -1) {
              const matchResult = child.props[key]?.toString().match(TableData.dataFieldKey);

              if (rowData && matchResult && matchResult?.length > 0) {
                props[`${key}Original`] = child.props[key];

                const dataField = matchResult[1];
                let newValue = this.getValueFromPath(rowData, dataField, rowIndex) ?? "";

                if (Array.isArray(newValue) || _.isObject(newValue)) {
                  props[key] = newValue;
                } else {
                  props[key] = child.props[key]?.toString().replace(TableData.oldValue, newValue);
                }

                if (
                  !window ||
                  !window.kuika ||
                  (window.kuika?.dashboardState !== 1 && window.kuika?.dashboardState !== 17)
                ) {
                  if ((key === "dataSource" && props[key] == "") || props[key] == "null" || props[key] == "undefined") {
                    props[key] = [];
                  } else if (props[key] == "" || props[key] == "null" || props[key] == "undefined") {
                    props[key] = " ";
                  }
                }
              }
            }

            if (key === "children") {
              manipulatedChildren.push(props.children);
            }
          } else if (key === "children") {
            if (TableData.getIsDataRepeater(child)) {
              return;
            }

            manipulatedChildren = TableData.setDataFieldValues(child.props.children, rowData, rowIndex);
          }

          if (!props[key]) {
            props[key] = child.props[key];
          }
        });
      }

      props.rowIndex = rowIndex;
      props.isTableData = true;

      // Take the cell's value back from the (never-seeded) screen-level form
      // field. A row-bound cell keeps its row value unconditionally — one field
      // cannot address one row, so a single write must not blank every row
      // (KOA-18127). Every other cell keeps its own value only while the field is
      // unseeded, so an explicit write still wins (KOA-18281).
      if (child?.type === Form.Item && child.props?.name !== undefined && child.props?.getValueProps === undefined) {
        const valuePropName: string = child.props?.valuePropName ?? "value";
        if (TableData.hasRowBoundValueProp(child.props?.children, valuePropName) === true) {
          props.getValueProps = TableData.keepRowSubstitutedValue;
        } else {
          props.getValueProps = TableData.deferToFormOnlyWhenSeeded(valuePropName);
        }
      }

      if (manipulatedChildren?.length > 0) {
        // antd Form.Item injects value/onChange into its child only when
        // `children` is a single React element; a length-1 array silently
        // disables the injection, so editable-cell picks never reach the form
        // and action bindings read empty values. Preserve Form.Item's
        // single-child contract by unwrapping the singleton.
        if (child.type === Form.Item && manipulatedChildren.length === 1) {
          return React.cloneElement(child, props, manipulatedChildren[0]);
        }
        return React.cloneElement(child, props, manipulatedChildren);
      }

      return React.cloneElement(child, props, child.props.children);
    });
  };

  render = () => {
    if (this.props.visibility === "hidden") return <></>;
    return TableData.setDataFieldValues(
      this.props.children,
      this.props.rowData,
      this.props.rowIndex,
      this.props.visibilityCondition,
      this.props.editabilityCondition
    );
  };
}

const tableData = withCommonEvents<TableDataProps & CommonProps, KTableDataState>(TableData);
export { tableData as TableData };
// withCommonEvents does not hoist statics; tests exercise
// TableData.setDataFieldValues through this named class export.
export { TableData as TableDataClass };
